import { Component, computed, ElementRef, inject, input } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';

import { AI_MESSAGES_INTERCEPTOR } from '../../di';
import { AiChatMessage } from '../../models/ai-chat-message.model';
import { copyIcon } from '../../functions/utils.ai';
import { getToolLabel } from '../../functions/tool-labels.ai';
import { ChatToolIndicatorComponent } from '../chat-loading-indicator/chat-loading-indicator.component';

function escapeHtml(value: string) {
	return value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
}

@Component({
	selector: 'ai-message-bubble',
	template: `
		<div class="ai-message-row" [class]="message().role">
			@if (avatarSrc()) {
				<img class="ai-message-avatar" [src]="avatarSrc()" [ngStyle]="avatarStyle()" alt="" />
			}
			<div class="ai-message-bubble" [ngStyle]="bubbleStyle()">
				<div class="ai-message-text" (click)="onContentClick($event)">
					@if (message().pending && !message().text) {
						<ai-chat-tool-indicator [toolLabel]="pendingToolLabel()" />
					} @else {
						<span class="stream-wrapper" [innerHTML]="content()"></span>
					}
					@if (message().role === 'ai' && !message().streaming && (message().text || message().html)) {
						<div class="stream-actions">
							<span
								class="stream-actions-copy"
								[title]="'Actions.Copy' | translate"
								[innerHTML]="copyIcon"
							></span>
						</div>
					}
				</div>
			</div>
		</div>
	`,
	styles: `
		.ai-message-row {
			display: flex;
			align-items: flex-start;
			gap: 8px;
			padding: 6px 12px;
		}
		.ai-message-row.user {
			flex-direction: row-reverse;
		}
		.ai-message-avatar {
			border-radius: 50%;
			flex: 0 0 auto;
		}
		.ai-message-bubble {
			border-radius: 12px;
			padding: 8px 12px;
			white-space: pre-wrap;
			word-break: break-word;
		}
		.ai-message-text :is(p, ol, ul) {
			margin: 0 0 0.4em;
		}
		.ai-message-text :is(p, ol, ul):last-child {
			margin-bottom: 0;
		}
		.ai-message-text li {
			margin: 0;
		}
		.stream-actions {
			text-align: right;
			margin-top: 4px;
		}
		.stream-actions-copy {
			cursor: pointer;
			opacity: .7;
		}
		.stream-actions-copy:hover {
			opacity: 1;
		}
		.ai-message-text ::ng-deep .ai-chat-suggestion-button {
			display: block;
			width: 100%;
			margin-top: 6px;
			padding: 8px 14px;
			border: 1px solid rgba(21, 90, 167, .3);
			border-radius: 14px;
			background: #fff;
			color: #155aa7;
			font-family: inherit;
			font-size: 0.9em;
			text-align: left;
			cursor: pointer;
			transition: background-color .15s, border-color .15s;
		}
		.ai-message-text ::ng-deep .ai-chat-suggestion-button:first-child {
			margin-top: 0;
		}
		.ai-message-text ::ng-deep .ai-chat-suggestion-button:hover {
			background: rgba(21, 90, 167, .08);
			border-color: rgba(21, 90, 167, .55);
		}
	`,
	imports: [CommonModule, TranslateModule, ChatToolIndicatorComponent],
})
export class AIMessageBubbleComponent {
	i18n = inject(TranslateService)
	interceptor = inject(AI_MESSAGES_INTERCEPTOR)
	sanitizer = inject(DomSanitizer)
	el = inject(ElementRef)

	message = input.required<AiChatMessage>()
	avatars = input<Record<string, { src: string, style: Record<string, string> }>>()
	messageStyles = input<Record<string, Record<string, string>>>()

	copyIcon = this.sanitizer.bypassSecurityTrustHtml(copyIcon)

	avatarSrc = computed(() => this.avatars()?.[this.message().role]?.src)
	avatarStyle = computed(() => this.avatars()?.[this.message().role]?.style)

	pendingToolLabel = computed(() => {
		const pending = this.message().pending
		return (pending && typeof pending === 'object' && 'tool' in pending) ? getToolLabel(pending.tool, this.i18n) : null
	})

	bubbleStyle = computed(() => ({
		...this.messageStyles()?.['shared'],
		...this.messageStyles()?.[this.message().role],
	}))

	// Bypasses Angular's HTML sanitizer: the interceptor already escapes user-provided fields
	// (entity names, ids) before generating this markup, and Angular's default sanitizer allowlist
	// strips interactive elements like <button> that the entity/export badges rely on.
	content = computed(() => {
		const m = this.message()
		const html = m.html || escapeHtml(m.text || '')
		return this.sanitizer.bypassSecurityTrustHtml(html)
	})

	onContentClick(event: MouseEvent) {
		const target = event.target as HTMLElement
		if (!target) return;
		if (target.closest('.stream-actions-copy')) {
			this.interceptor.handleClipboardCopy(event)
			return;
		}
		if (target.closest('.ai-entity-token') || target.closest('.ai-export-token')) {
			this.interceptor.handleEntityClick(event)
		}
	}
}
